
import React, { useEffect, useState, useContext } from "react";
import { AuthContext } from "../../../Contexts/AuthContext";
import { updateUser } from "../../../api/usersApi";
import { useTranslation } from "react-i18next";
import { FaUser, FaEnvelope, FaEye, FaEyeSlash } from "react-icons/fa";
import { RiLockPasswordLine } from "react-icons/ri";
import { TbPasswordUser } from "react-icons/tb";
import { PiPassword } from "react-icons/pi";
import "./profile.css";



const getInitials = (name) =>
    (name || "U").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();

const getStrength = (pw) => {
    if (!pw) return 0;
    let s = 0;
    if (pw.length >= 8) s++;
    if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
    if (/[0-9]/.test(pw)) s++;
    if (/[^A-Za-z0-9]/.test(pw)) s++;
    return s;
};

const STRENGTH_LABELS = ["", "Weak", "Fair", "Good", "Strong"];
const STRENGTH_CLS    = ["", "s1",   "s2",   "s3",   "s4"];


export default function Profile() {
    const { user, setUser, logout } = useContext(AuthContext);
    const { t, i18n } = useTranslation();

    const [form, setForm] = useState({
        nom: "", email: "", role: "",
        current_password: "", password: "", password_confirmation: "",
    });
    const [showPassword, setShowPassword] = useState({
        current: false,
        new: false,
        confirm: false
    });
    const [confirmTouched, setConfirmTouched] = useState(false);
    const [errors, setErrors]   = useState({});
    const [loading, setLoading] = useState(false);
    const [toast, setToast]     = useState(null);

    useEffect(() => {
        if (user) setForm({
            nom: user.nom || "", email: user.email || "", role: user.role || "",
            current_password: "", password: "", password_confirmation: "",
        });
    }, [user]);

    const handle = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    const handleConfirmBlur = () => setConfirmTouched(true);
    const toggleShow = (field) => setShowPassword(s => ({ ...s, [field]: !s[field] }));

    const showToast = (msg, type = "success") => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3400);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setLoading(true);
        setErrors({});

        const payload = { nom: form.nom, email: form.email };
        if (form.password.trim()) {
            payload.current_password      = form.current_password;
            payload.password              = form.password;
            payload.password_confirmation = form.password_confirmation;
        }

        updateUser(user.id, payload)
            .then((updated) => {
                setLoading(false);
                if (form.password) {
                    showToast("Password updated — logging you out.");
                    setTimeout(logout, 1500);
                } else {
                    setUser(updated);
                    showToast("Profile saved successfully.");
                }
            })
            .catch((err) => {
                setLoading(false);
                setErrors(err.response?.data?.errors || {});
                showToast("Something went wrong.", "error");
            });
    };

    if (!user) return <div style={{ padding: 40 }}>{t("profile.loading")}</div>;

    const strength   = getStrength(form.password);
    const matchOk    = form.password && form.password_confirmation && form.password === form.password_confirmation;
    const matchBad   = form.password_confirmation && form.password !== form.password_confirmation;
    const initials   = getInitials(form.nom);

    return (
        <>
            <div className="pf-wrap">
                <div className="pf-container">
                    {/* Header */}
                    <div className="pf-header">
                        <div className="pf-avatar-ring">
                            <div className="pf-avatar">{initials}</div>
                            <div className="pf-avatar-edit">✏️</div>
                        </div>
                        <div>
                            <div className="pf-header-title">{form.nom || t("profile.settings")}</div>
                            <div className="pf-header-sub">{t("profile.manageAccount")}</div>
                        </div>
                    </div>
                    <form onSubmit={handleSubmit}>
                        {/* Account Information */}
                        <div className="pf-card">
                            <div className="pf-card-head">
                                <div className="pf-card-icon green"><FaUser /></div>
                                <div>
                                    <div className="pf-card-title">{t("profile.accountInfo")}</div>
                                    <div className="pf-card-desc">{t("profile.updateNameEmail")}</div>
                                </div>
                            </div>
                            <div className="pf-card-body">
                                <div className="pf-row">
                                    <div className="pf-group">
                                        <label className="pf-label">{t("profile.fullName")}</label>
                                        <div className="pf-input-wrap">
                                            <input className="pf-input has-icon" type="text" name="nom"
                                                value={form.nom} onChange={handle} placeholder={t("profile.fullNamePlaceholder")}/>
                                            <span className="pf-suffix"><FaUser /></span>
                                        </div>
                                        {errors.nom && <div className="pf-error">⚠ {errors.nom}</div>}
                                    </div>
                                    <div className="pf-group">
                                        <label className="pf-label">
                                            {t("profile.role")} <span className="pf-badge">{t("profile.readonly")}</span>
                                        </label>
                                        <div className="pf-input-wrap">
                                            <input className="pf-input has-icon" type="text"
                                                value={form.role} disabled />
                                            <span className="pf-suffix">🔒</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="pf-group">
                                    <label className="pf-label">{t("profile.email")}</label>
                                    <div className="pf-input-wrap">
                                        <input className="pf-input has-icon" type="email" name="email"
                                            value={form.email} onChange={handle} placeholder={t("profile.emailPlaceholder")}/>
                                        <span className="pf-suffix"><FaEnvelope /></span>
                                    </div>
                                    {errors.email && <div className="pf-error">⚠ {errors.email}</div>}
                                </div>
                            </div>
                        </div>
                        {/* Change Password */}
                        <div className="pf-card">
                            <div className="pf-card-head">
                                <div className="pf-card-icon amber"><RiLockPasswordLine /></div>
                                <div>
                                    <div className="pf-card-title">{t("profile.changePassword")}</div>
                                    <div className="pf-card-desc">{t("profile.leaveBlank")}</div>
                                </div>
                            </div>
                            <div className="pf-card-body">
                                <div className="pf-group">
                                    <label className="pf-label">{t("profile.currentPassword")}</label>
                                    <div className="pf-input-wrap">
                                        <input className="pf-input has-icon" type={showPassword.current ? "text" : "password"} name="current_password"
                                            value={form.current_password} onChange={handle} placeholder={t("profile.currentPasswordPlaceholder")}/>
                                        <span className="pf-suffix" style={{cursor:'pointer'}} onClick={()=>toggleShow('current')} tabIndex={0} role="button" aria-label={showPassword.current ? t("profile.hidePassword") : t("profile.showPassword") }>
                                            {showPassword.current ? <FaEyeSlash /> : <FaEye />}
                                        </span>
                                    </div>
                                    {errors.current_password && (
                                        <div className="pf-error">⚠ {errors.current_password}</div>
                                    )}
                                </div>
                                <div className="pf-divider" />
                                <div className="pf-row">
                                    <div className="pf-group">
                                        <label className="pf-label">{t("profile.newPassword")}</label>
                                        <div className="pf-input-wrap">
                                            <input className="pf-input has-icon" type={showPassword.new ? "text" : "password"} name="password"
                                                value={form.password} onChange={handle} placeholder={t("profile.newPasswordPlaceholder")}/>
                                            <span className="pf-suffix" style={{cursor:'pointer'}} onClick={()=>toggleShow('new')} tabIndex={0} role="button" aria-label={showPassword.new ? t("profile.hidePassword") : t("profile.showPassword") }>
                                                {showPassword.new ? <FaEyeSlash /> : <FaEye />}
                                            </span>
                                        </div>
                                        {form.password && (
                                            <>
                                                <div className="pf-strength">
                                                    {[1,2,3,4].map(i => (
                                                        <div key={i}
                                                            className={`pf-strength-bar${i <= strength ? " " + STRENGTH_CLS[strength] : ""}`}
                                                        />
                                                    ))}
                                                </div>
                                                <div className="pf-strength-text">{t(`profile.strength.${STRENGTH_LABELS[strength].toLowerCase()}`)}</div>
                                            </>
                                        )}
                                        {errors.password && <div className="pf-error">⚠ {errors.password}</div>}
                                    </div>
                                    <div className="pf-group">
                                        <label className="pf-label">{t("profile.confirmPassword")}</label>
                                        <div className="pf-input-wrap" style={{ display: 'flex', alignItems: 'center' }}>
                                            <input className="pf-input has-icon" type={showPassword.confirm ? "text" : "password"} name="password_confirmation"
                                                value={form.password_confirmation} onChange={handle}
                                                onBlur={handleConfirmBlur}
                                                placeholder={t("profile.confirmPasswordPlaceholder")}
                                                style={confirmTouched ? (matchOk ? { borderColor: "var(--green-400)" }
                                                     : matchBad ? { borderColor: "#ef4444" } : {}) : {}} />
                                            <span className="pf-suffix" style={{cursor:'pointer', marginLeft: 8}} onClick={()=>toggleShow('confirm')} tabIndex={0} role="button" aria-label={showPassword.confirm ? t("profile.hidePassword") : t("profile.showPassword") }>
                                                {showPassword.confirm ? <FaEyeSlash /> : <FaEye />}
                                            </span>
                                            {confirmTouched && (matchOk || matchBad) && (
                                                <span className="pf-suffix" style={{ marginLeft: 8 }}>{matchOk ? "✅" : "❌"}</span>
                                            )}
                                        </div>
                                        {confirmTouched && matchBad && <div className="pf-error">⚠ {t("profile.passwordsDontMatch")}</div>}
                                        {errors.password_confirmation && <div className="pf-error">⚠ {errors.password_confirmation}</div>}
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* Actions */}
                        <div className="pf-actions">
                            <button type="button" className="pf-btn pf-btn-ghost"
                                onClick={() => setForm(f => ({ ...f, current_password: "", password: "", password_confirmation: "" }))}>
                                {t("profile.discard")}
                            </button>
                            <button type="submit" className="pf-btn pf-btn-primary"
                                disabled={loading || !!matchBad}>
                                {loading
                                    ? <><div className="pf-spinner" /> {t("profile.saving")}</>
                                    : t("profile.saveChanges")}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
            {toast && (
                <div className={`pf-toast${toast.type === "error" ? " error" : ""}`}>
                    <span>{toast.type === "error" ? "⚠️" : "✅"}</span>
                    {toast.msg}
                </div>
            )}
        </>
    );
}